// No lodash imports needed - using native JavaScript
import {
  $expression,
  type Collection,
  type DocumentValue,
} from './expressions';

// Import complex types from main index for now
import type { Expression, AccumulatorExpression } from '../index';

// Local type definitions for accumulator functions
type AccumulatorFunction = (
  collection: Collection,
  spec: Expression
) => DocumentValue | ReadonlyArray<DocumentValue>;
type AccumulatorOperatorObject =
  | AccumulatorExpression
  | { [key: string]: Expression };

const ACCUMULATORS: Record<string, AccumulatorFunction> = {
  $accumulate,
  $sum,
  $avg,
  $first,
  $last,
  $min,
  $max,
  $push,
  $addToSet,
  $stdDevPop,
  $stdDevSamp,
  $variancePop,
  $varianceSamp,
};

function isAccumulatorExpression(
  expression: any
): expression is AccumulatorOperatorObject {
  const expressionKeys = Object.keys(expression);
  return (
    typeof expression === 'object' &&
    expression !== null &&
    expressionKeys.length === 1 &&
    expressionKeys[0]! in ACCUMULATORS
  );
}

/**
 * Accumulators for aggregation operations
 */

function $accumulate(
  collection: Collection,
  operatorExpression: Expression
): DocumentValue | ReadonlyArray<DocumentValue> {
  if (isAccumulatorExpression(operatorExpression)) {
    const [operator] = Object.keys(operatorExpression);
    const args = (operatorExpression as any)[
      operator as keyof AccumulatorExpression
    ] as Expression;
    const accumulatorFunction = ACCUMULATORS[operator!];

    if (!accumulatorFunction) {
      throw new Error(`Unknown accumulator: ${operator}`);
    }

    return accumulatorFunction(collection, args);
  }

  throw new Error('Invalid accumulator expression');
}

function $sum(collection: Collection, spec: Expression): number {
  if (spec === 1) {
    return collection.length;
  }

  // Calculate sum manually to handle expressions properly
  let total = 0;
  for (const obj of collection) {
    // C) $$ROOT resolution: Pass obj as both current document and root
    const value = $expression(obj, spec, obj);
    if (typeof value === 'number' && !isNaN(value)) {
      total += value;
    }
  }
  return total;
}

function $avg(collection: Collection, spec: Expression): number {
  const totalSum = $sum(collection, spec);
  const count = collection.length;
  return count > 0 ? totalSum / count : 0;
}

function $first(collection: Collection, spec: Expression): DocumentValue {
  const firstDoc = collection[0];
  // C) $$ROOT resolution: Pass firstDoc as both current document and root
  return firstDoc ? $expression(firstDoc, spec, firstDoc) : null;
}

function $last(collection: Collection, spec: Expression): DocumentValue {
  const lastDoc = collection[collection.length - 1];
  // C) $$ROOT resolution: Pass lastDoc as both current document and root
  return lastDoc ? $expression(lastDoc, spec, lastDoc) : null;
}

function $max(collection: Collection, spec: Expression): number | null {
  let maxValue = -Infinity;
  for (const obj of collection) {
    // C) $$ROOT resolution: Pass obj as both current document and root
    const value = $expression(obj, spec, obj);
    if (typeof value === 'number' && value > maxValue) {
      maxValue = value;
    }
  }
  return maxValue === -Infinity ? null : maxValue;
}

function $min(collection: Collection, spec: Expression): number | null {
  let minValue = Infinity;
  for (const obj of collection) {
    // C) $$ROOT resolution: Pass obj as both current document and root
    const value = $expression(obj, spec, obj);
    if (typeof value === 'number' && value < minValue) {
      minValue = value;
    }
  }
  return minValue === Infinity ? null : minValue;
}

function $push(
  collection: Collection,
  spec: Expression
): ReadonlyArray<DocumentValue> {
  // $$ROOT fast path: return the original documents
  if (spec === '$$ROOT') {
    return collection as ReadonlyArray<DocumentValue>;
  }

  // Preserve explicit undefined vs missing for field paths
  if (typeof spec === 'string' && spec.startsWith('$')) {
    const path = spec.slice(1);
    if (!path.includes('.')) {
      return collection.map(obj =>
        Object.prototype.hasOwnProperty.call(obj as any, path)
          ? (obj as any)[path]
          : null
      );
    } else {
      const segments = path.split('.');
      return collection.map(obj => {
        let cur: any = obj;
        for (const seg of segments) {
          if (cur && typeof cur === 'object' && seg in cur) {
            cur = cur[seg];
          } else {
            return null; // missing path → null
          }
        }
        return cur; // may be undefined explicitly
      });
    }
  }

  // General expression
  return collection.map(obj => $expression(obj, spec, obj));
}

function $addToSet(
  collection: Collection,
  spec: Expression
): ReadonlyArray<DocumentValue> {
  const values = $push(collection, spec);
  return [...new Set(values.map(obj => JSON.stringify(obj)))].map(str =>
    JSON.parse(str)
  );
}

/**
 * Standard Deviation Population using Welford's online algorithm
 * Returns null for empty sets, sqrt(variance) for non-empty sets
 * Numerically stable single-pass algorithm
 */
function $stdDevPop(collection: Collection, spec: Expression): number | null {
  let n = 0;
  let mean = 0;
  let M2 = 0;

  for (const obj of collection) {
    const value = $expression(obj, spec, obj);

    // Skip non-numeric values (MongoDB behavior)
    if (typeof value !== 'number' || isNaN(value)) {
      continue;
    }

    // Welford's algorithm - incremental update
    n++;
    const delta = value - mean;
    mean += delta / n;
    const delta2 = value - mean;
    M2 += delta * delta2;
  }

  // Return null for empty set
  if (n === 0) {
    return null;
  }

  // Population variance = M2 / n
  // Standard deviation = sqrt(variance)
  return Math.sqrt(M2 / n);
}

/**
 * Standard Deviation Sample using Welford's online algorithm
 * Returns null for sets with 0 or 1 elements
 * Uses (n-1) divisor for sample variance (Bessel's correction)
 */
function $stdDevSamp(collection: Collection, spec: Expression): number | null {
  let n = 0;
  let mean = 0;
  let M2 = 0;

  for (const obj of collection) {
    const value = $expression(obj, spec, obj);

    // Skip non-numeric values (MongoDB behavior)
    if (typeof value !== 'number' || isNaN(value)) {
      continue;
    }

    // Welford's algorithm - incremental update
    n++;
    const delta = value - mean;
    mean += delta / n;
    const delta2 = value - mean;
    M2 += delta * delta2;
  }

  // Return null for sets with 0 or 1 elements (can't compute sample std dev)
  if (n <= 1) {
    return null;
  }

  // Sample variance = M2 / (n - 1)
  // Standard deviation = sqrt(variance)
  return Math.sqrt(M2 / (n - 1));
}

/**
 * Variance Population - companion to $stdDevPop
 * Returns the variance without taking the square root
 */
function $variancePop(collection: Collection, spec: Expression): number | null {
  let n = 0;
  let mean = 0;
  let M2 = 0;

  for (const obj of collection) {
    const value = $expression(obj, spec, obj);

    // Skip non-numeric values
    if (typeof value !== 'number' || isNaN(value)) {
      continue;
    }

    // Welford's algorithm
    n++;
    const delta = value - mean;
    mean += delta / n;
    const delta2 = value - mean;
    M2 += delta * delta2;
  }

  if (n === 0) {
    return null;
  }

  return M2 / n;
}

/**
 * Variance Sample - companion to $stdDevSamp
 * Returns the sample variance without taking the square root
 */
function $varianceSamp(
  collection: Collection,
  spec: Expression
): number | null {
  let n = 0;
  let mean = 0;
  let M2 = 0;

  for (const obj of collection) {
    const value = $expression(obj, spec, obj);

    // Skip non-numeric values
    if (typeof value !== 'number' || isNaN(value)) {
      continue;
    }

    // Welford's algorithm
    n++;
    const delta = value - mean;
    mean += delta / n;
    const delta2 = value - mean;
    M2 += delta * delta2;
  }

  if (n <= 1) {
    return null;
  }

  return M2 / (n - 1);
}

export { $accumulate };
export default ACCUMULATORS;
